import type { LivingScene } from '@/contracts/content';

/**
 * The living-scene timeline (spec §10.2).
 *
 * This lives on its own because TWO things play it: the animation on screen,
 * and the video export. If each resolved the timeline itself they would drift,
 * and the file a family keeps would stop being the thing they watched. One
 * function, one answer, at any time t.
 */

export const EASINGS = {
  linear: (t: number) => t,
  'ease-out': (t: number) => 1 - Math.pow(1 - t, 3),
  'ease-in-out': (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
};

export interface ArtworkTransform {
  /** Percentages of the artwork's own size, matching the CSS transform. */
  x: number;
  y: number;
  scale: number;
  rotate: number;
  opacity: number;
}

/** Resolves the artwork's authored transform at time `t` (ms). */
export function artworkTransformAt(scene: LivingScene, t: number): ArtworkTransform {
  const state: ArtworkTransform = { x: 0, y: 0, scale: 1, rotate: 0, opacity: 1 };
  for (const step of scene.steps) {
    if (step.target !== 'artwork') continue;
    if (t < step.startMs) continue;
    const span = Math.max(1, step.endMs - step.startMs);
    const raw = Math.min(1, (t - step.startMs) / span);
    const e = EASINGS[step.easing ?? 'linear'](raw);
    const lerp = (a: number | undefined, b: number | undefined, fallback: number) => {
      if (a === undefined && b === undefined) return undefined;
      const from = a ?? fallback;
      const to = b ?? from;
      return from + (to - from) * e;
    };
    const x = lerp(step.from.x, step.to.x, state.x);
    const y = lerp(step.from.y, step.to.y, state.y);
    const s = lerp(step.from.scale, step.to.scale, state.scale);
    const r = lerp(step.from.rotate, step.to.rotate, state.rotate);
    const o = lerp(step.from.opacity, step.to.opacity, state.opacity);
    if (x !== undefined) state.x = x;
    if (y !== undefined) state.y = y;
    if (s !== undefined) state.scale = s;
    if (r !== undefined) state.rotate = r;
    if (o !== undefined) state.opacity = o;
  }
  return state;
}
